import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { PricingTable } from './components/PricingTable';
import { Uploader } from './components/Uploader';
import { SettingsStep } from './components/SettingsStep';
import { ProcessingStepper } from './components/ProcessingStepper';
import { CutPlanEditor } from './components/CutPlanEditor';
import { RenderStep } from './components/RenderStep';
import { DashboardView } from './components/DashboardView';
import { CookieBanner } from './components/CookieBanner';
import { LegalView, type LegalTabType } from './components/LegalView';
import { UserSettingsModal } from './components/UserSettingsModal';
import { ToastContainer, toast } from './components/Toast';
import { BetaBanner } from './components/BetaBanner';
import { BetaDisclaimerModal } from './components/BetaDisclaimerModal';
import { Footer } from './components/Footer';
import { GuidesView } from './components/GuidesView';
import { renderVideoCutsInBrowser } from './utils/browserVideoCutter';
import type { User, UserQuotaInfo, Job, CutRange, PlanType, CutPlan } from '@vidly/types';
import { PLAN_CONFIGS } from '@vidly/config';

// Helper to extract true duration of any uploaded video file
const getExactVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;
    video.onloadedmetadata = () => {
      const dur = video.duration;
      resolve(dur > 0 && isFinite(dur) ? Math.round(dur) : 38);
    };
    video.onerror = () => {
      resolve(38);
    };
  });
};

// Helper to parse query parameters on load
const getInitialView = (): 'home' | 'dashboard' | 'workflow' | 'pricing' | 'legal' | 'guides' => {
  if (typeof window === 'undefined') return 'home';
  const params = new URLSearchParams(window.location.search);
  const view = params.get('view');
  if (view && ['home', 'dashboard', 'workflow', 'pricing', 'legal', 'guides'].includes(view)) {
    return view as any;
  }
  return 'home';
};

const getInitialLegalTab = (): LegalTabType => {
  if (typeof window === 'undefined') return 'privacy';
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab');
  if (tab && ['privacy', 'terms', 'cookies', 'faq'].includes(tab)) {
    return tab as LegalTabType;
  }
  return 'privacy';
};

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [quota, setQuota] = useState<UserQuotaInfo | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentView, setCurrentView] = useState<'home' | 'dashboard' | 'workflow' | 'pricing' | 'legal' | 'guides'>(getInitialView);
  const [workflowStep, setWorkflowStep] = useState<'upload' | 'settings' | 'processing' | 'review' | 'render'>('upload');
  
  // Active job in the workflow
  const [activeJob, setActiveJob] = useState<Job | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadFilename, setDownloadFilename] = useState<string>('resumen.mp4');
  const [localVideoUrl, setLocalVideoUrl] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);
  const [showSettings, setShowSettings] = useState(false);

  // Dedicated legal page tab state
  const [legalTab, setLegalTab] = useState<LegalTabType>(getInitialLegalTab);

  // Navigation handler synchronized with browser history and URL query parameters
  const handleNavigate = (view: 'home' | 'dashboard' | 'pricing' | 'legal' | 'guides', tab?: LegalTabType) => {
    if (tab) {
      setLegalTab(tab);
    }
    setCurrentView(view);

    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (view === 'home') {
        params.delete('view');
        params.delete('tab');
      } else {
        params.set('view', view);
        if (tab) params.set('tab', tab);
        else params.delete('tab');
      }
      const newQuery = params.toString();
      const newUrl = newQuery ? `/?${newQuery}` : '/';
      window.history.pushState({}, '', newUrl);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Beta statistics state
  const [betaStats, setBetaStats] = useState<{
    totalLimit: number;
    completedCount: number;
    remainingCount: number;
    feedbackCount: number;
  }>({
    totalLimit: 100,
    completedCount: 0,
    remainingCount: 100,
    feedbackCount: 0
  });

  const fetchBetaStats = async () => {
    try {
      const res = await fetch('/api/beta/stats');
      if (res.ok) {
        const data = await res.json();
        setBetaStats(data);
      }
    } catch {}
  };

  // Check user session on initial load
  const fetchCurrentUser = async () => {
    try {
      const res = await fetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        const userData = {
          ...data.user,
          picture: data.user.picture || data.user.avatar || null,
          avatar: data.user.avatar || data.user.picture || null,
        };
        setUser(userData);
        setQuota(data.quota);
        const hasExplicitView = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('view');
        if (currentView === 'home' && !hasExplicitView) {
          setCurrentView('dashboard');
        }
        return;
      }
    } catch {
      // Backend not running yet or unauthenticated
    }

    // Client-side cookie fallback
    const match = document.cookie.match(/vidly_token=([^;]+)/);
    if (match) {
      try {
        const decoded = JSON.parse(atob(match[1]));
        const userData = {
          ...decoded,
          picture: decoded.picture || decoded.avatar || null,
          avatar: decoded.avatar || decoded.picture || null,
        };
        setUser(userData);
        setQuota({
          plan: decoded.plan || 'free',
          maxDailyVideos: decoded.plan === 'premium' ? 50 : 3,
          videosUsedToday: 0,
          videosRemainingToday: decoded.plan === 'premium' ? 50 : 3,
          maxOriginalDurationSeconds: decoded.plan === 'premium' ? 7200 : 900,
          maxOutputDurationSeconds: decoded.plan === 'premium' ? 1800 : 180,
          maxUploadSizeBytes: decoded.plan === 'premium' ? 2147483648 : 524288000,
          adsEnabled: decoded.plan !== 'premium',
          historyRetentionDays: decoded.plan === 'premium' ? 30 : 1,
          activeJobsCount: 0,
          maxConcurrentJobs: decoded.plan === 'premium' ? 3 : 1
        });
        const hasExplicitView = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('view');
        if (currentView === 'home' && !hasExplicitView) {
          setCurrentView('dashboard');
        }
      } catch {}
    }
  };

  const fetchJobs = async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/jobs');
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || []);
      }
    } catch (e) {
      console.error('Error fetching jobs:', e);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchBetaStats();

    // Listen to browser Back / Forward history navigation
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const viewParam = params.get('view') as any;
      const tabParam = params.get('tab') as LegalTabType;
      if (viewParam && ['home', 'dashboard', 'workflow', 'pricing', 'legal', 'guides'].includes(viewParam)) {
        setCurrentView(viewParam);
      } else {
        setCurrentView('home');
      }
      if (tabParam && ['privacy', 'terms', 'cookies', 'faq'].includes(tabParam)) {
        setLegalTab(tabParam);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (user) {
      fetchJobs();
    }
  }, [user]);

  // Handle Stripe success redirect and OAuth callback on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgradeParam = params.get('upgrade');
    const canceledParam = params.get('canceled');
    
    if (upgradeParam === 'success') {
      // Activate premium in session and re-fetch user
      fetch('/api/billing/upgrade-dev', { method: 'POST' })
        .catch(() => {})
        .finally(() => {
          fetchCurrentUser().then(() => {
            setCurrentView('dashboard');
            window.history.replaceState({}, '', '/');
            setTimeout(() => {
              toast.success('¡Suscripción Premium activa!', 'Tu cuenta ha sido actualizada con éxito.');
            }, 500);
          });
        });
    }
    
    if (canceledParam === 'true') {
      window.history.replaceState({}, '', '/');
    }

    // Handle Google OAuth callback token in URL hash
    const authToken = params.get('auth_token');
    if (authToken) {
      // Store the JWT and refresh user session
      document.cookie = `vidly_token=${authToken}; path=/; max-age=${7 * 24 * 3600}; SameSite=Lax`;
      window.history.replaceState({}, '', '/');
      fetchCurrentUser();
    }
  }, []);

  // Poll status when activeJob is processing or rendering (Backend connected)
  useEffect(() => {
    if (!activeJob) return;
    if (['uploading', 'validating', 'transcribing', 'planning', 'rendering'].includes(activeJob.status)) {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`/api/jobs/${activeJob.id}`);
          if (res.ok) {
            const data = await res.json();
            setActiveJob(data.job);
            if (data.downloadUrl) {
              setDownloadUrl(data.downloadUrl);
            }

            if (data.job.status === 'review_pending') {
              setWorkflowStep('review');
            } else if (data.job.status === 'completed' || data.job.status === 'failed') {
              setWorkflowStep('render');
              fetchCurrentUser();
            }
          }
        } catch (e) {
          console.error('Polling error:', e);
        }
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [activeJob]);

  // Subtle scroll reveal effect for sections
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.08,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [currentView, workflowStep]);

  // Handle Google Login
  const handleGoogleLogin = () => {
    const clientId = '813993465809-r1iagc7hstvpvc6nu7ocjtb1a6ctblps.apps.googleusercontent.com';
    const redirectUri = window.location.origin + '/api/auth/google/callback';
    const state = 'oauth_' + Math.random().toString(36).substring(2);
    sessionStorage.setItem('vidly_oauth_state', state);
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile&state=${state}&access_type=online&prompt=select_account`;
    window.location.href = authUrl;
  };

  // Handle Logout
  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    setQuota(null);
    setCurrentView('home');
  };

  // Step 1: Uploading Video (Uploads to local FFmpeg API when in dev mode)
  const handleFileSelected = async (file: File) => {
    if (!user) {
      toast.info('Inicio de sesión requerido', 'Inicia sesión con Google para procesar tu vídeo en la beta.');
      handleGoogleLogin();
      return;
    }

    setUploadedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setLocalVideoUrl(objectUrl);

    setIsUploading(true);
    setUploadProgress(20);

    let detectedDuration = await getExactVideoDuration(file);
    let resolvedJobId = 'job_' + Date.now().toString(36);

    // Attempt real upload to local Vite FFmpeg API
    try {
      setUploadProgress(50);
      const localUpload = await fetch('/api/local/upload', {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'video/mp4' },
        body: file
      });

      if (localUpload.ok) {
        const upData = await localUpload.json();
        resolvedJobId = upData.jobId;
        detectedDuration = upData.durationSeconds || detectedDuration;
      }
    } catch (e) {
      console.warn('Local API upload unavailable, using browser metadata:', e);
    }

    setUploadProgress(100);
    setIsUploading(false);

    const targetDuration = Math.max(5, Math.min(Math.round(detectedDuration * 0.5), detectedDuration - 2));

    const newJob: Job = {
      id: resolvedJobId,
      userId: user.id,
      status: 'pending',
      sourceKey: `uploads/${user.id}/${file.name}`,
      durationSeconds: detectedDuration,
      targetDurationSeconds: targetDuration,
      settings: {
        instructions: '',
        targetDurationSeconds: targetDuration,
        mandatoryRanges: [],
        originalFilename: file.name
      },
      createdAt: Date.now(),
      updatedAt: Date.now(),
      expiresAt: Date.now() + 24 * 3600 * 1000
    };

    setActiveJob(newJob);
    setWorkflowStep('settings');
  };

  // Step 2: Submit Settings and Begin Analysis
  const handleSettingsSubmit = async (settings: {
    instructions: string;
    targetDurationSeconds: number;
    mandatoryRanges: CutRange[];
  }) => {
    if (!activeJob) return;
    setIsProcessing(true);

    setActiveJob({
      ...activeJob,
      status: 'validating',
      targetDurationSeconds: settings.targetDurationSeconds,
      settings: {
        ...activeJob.settings,
        ...settings
      }
    });
    setWorkflowStep('processing');
    setIsProcessing(false);

    // Processing stepper: validating -> transcribing -> planning -> review_pending
    setTimeout(() => {
      setActiveJob((prev) => prev ? { ...prev, status: 'transcribing' } : null);
    }, 1000);

    setTimeout(() => {
      setActiveJob((prev) => prev ? { ...prev, status: 'planning' } : null);
    }, 2000);

    setTimeout(async () => {
      // 1. Try Cloudflare Pages / API plan endpoint with Few-Shot Gemini AI
      try {
        const planRes = await fetch('/api/plan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId: activeJob.id,
            instructions: settings.instructions,
            targetDurationSeconds: settings.targetDurationSeconds,
            totalDurationSeconds: activeJob.durationSeconds || 60,
            mandatoryRanges: settings.mandatoryRanges
          })
        });

        if (planRes.ok) {
          const planData = await planRes.json();
          if (planData.plan && planData.plan.selectedRanges?.length) {
            setActiveJob((prev) => prev ? {
              ...prev,
              status: 'review_pending',
              aiPlan: planData.plan,
              provider: planData.providerUsed
            } : null);
            setWorkflowStep('review');
            return;
          }
        }
      } catch (err) {
        console.warn('API plan endpoint error, checking local:', err);
      }

      // 2. Try local Vite dev API plan endpoint
      try {
        const localRes = await fetch('/api/local/plan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId: activeJob.id,
            instructions: settings.instructions,
            targetDurationSeconds: settings.targetDurationSeconds,
            mandatoryRanges: settings.mandatoryRanges
          })
        });

        if (localRes.ok) {
          const planData = await localRes.json();
          if (planData.plan && planData.plan.selectedRanges?.length) {
            setActiveJob((prev) => prev ? {
              ...prev,
              status: 'review_pending',
              aiPlan: planData.plan,
              provider: planData.providerUsed
            } : null);
            setWorkflowStep('review');
            return;
          }
        }
      } catch (err) {
        console.warn('Local AI plan endpoint fallback:', err);
      }

      const totalDuration = activeJob.durationSeconds || 38;
      const selectedRanges: CutRange[] = [];

      // 1. Strictly include mandatory intervals first
      for (const mand of settings.mandatoryRanges) {
        selectedRanges.push({
          start: mand.start,
          end: Math.min(totalDuration, mand.end),
          reason: mand.reason || '🔒 Intervalo obligatorio protegido',
          isMandatory: true
        });
      }

      // 2. Add AI selected highlight cuts adapted to the real length and target duration
      const currentSum = selectedRanges.reduce((acc, r) => acc + (r.end - r.start), 0);
      const needed = Math.max(0, settings.targetDurationSeconds - currentSum);

      if (needed > 0) {
        const segCount = Math.max(2, Math.min(6, Math.ceil(needed / 45)));
        const segDuration = needed / segCount;
        const step = (totalDuration - segDuration) / (segCount + 1);

        for (let i = 0; i < segCount; i++) {
          const s = Math.min(totalDuration - 5, Math.max(0, (i + 0.4) * step));
          const e = Math.min(totalDuration, s + segDuration);
          if (e > s + 2 && !selectedRanges.some(r => Math.abs(r.start - s) < 10)) {
            let role = '💡 Punto clave';
            if (i === 0) role = '🎯 Gancho';
            else if (i === segCount - 1) role = '⚡ Conclusión';

            selectedRanges.push({
              start: Math.round(s * 10) / 10,
              end: Math.round(e * 10) / 10,
              reason: `${role}: ${i === 0 ? 'Planteamiento inicial del tema' : (i === segCount - 1 ? 'Síntesis final del metraje' : `Momento destacado #${i + 1}`)}`,
              isMandatory: false
            });
          }
        }
      }

      selectedRanges.sort((a, b) => a.start - b.start);

      const mockPlan: CutPlan = {
        targetDurationSeconds: settings.targetDurationSeconds,
        keepRanges: settings.mandatoryRanges,
        selectedRanges,
        estimatedDurationSeconds: Math.round(selectedRanges.reduce((acc, r) => acc + (r.end - r.start), 0) * 10) / 10
      };

      setActiveJob((prev) => prev ? {
        ...prev,
        status: 'review_pending',
        aiPlan: mockPlan
      } : null);

      setWorkflowStep('review');
    }, 3200);
  };

  // Step 4: Confirm Render with Real Local FFmpeg!
  const handleConfirmRender = async (finalRanges: CutRange[]) => {
    if (!activeJob || !quota) return;

    if (quota.videosRemainingToday <= 0) {
      toast.error(
        'Límite de cuota alcanzado',
        `Has alcanzado el límite diario de tu plan ${user?.plan.toUpperCase()}. Se renovará a medianoche.`
      );
      return;
    }

    // Deduct quota
    const updatedQuota: UserQuotaInfo = {
      ...quota,
      videosUsedToday: quota.videosUsedToday + 1,
      videosRemainingToday: Math.max(0, quota.videosRemainingToday - 1)
    };
    setQuota(updatedQuota);

    setActiveJob({
      ...activeJob,
      status: 'rendering'
    });
    setWorkflowStep('render');
    setIsRendering(true);
    setRenderProgress(10);
    toast.info('Renderizado en proceso', 'Iniciando el ensamblado de los fragmentos...');

    // 1. Attempt real local FFmpeg cut rendering on the machine (dev mode)
    try {
      const renderRes = await fetch('/api/local/render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId: activeJob.id,
          ranges: finalRanges
        })
      });

      if (renderRes.ok) {
        const renderData = await renderRes.json();
        setDownloadUrl(renderData.downloadUrl);
        setDownloadFilename(`resumen-${activeJob.id.substring(0, 8)}.mp4`);
        setRenderProgress(100);
        setIsRendering(false);
        setActiveJob((prev) => prev ? {
          ...prev,
          status: 'completed',
          outputKey: renderData.downloadUrl,
          durationSeconds: renderData.durationSeconds || activeJob.durationSeconds,
          aiPlan: {
            targetDurationSeconds: activeJob.targetDurationSeconds || 18,
            keepRanges: activeJob.settings.mandatoryRanges,
            selectedRanges: finalRanges,
            estimatedDurationSeconds: renderData.durationSeconds || 18
          }
        } : null);
        fetchBetaStats();
        toast.success('¡Vídeo completado!', 'Tu resumen ha sido renderizado con FFmpeg y está listo para descargar.');
        return;
      }
    } catch (err) {
      console.warn('Local FFmpeg render call unavailable, switching to in-browser video editor:', err);
    }

    // 2. Client-Side In-Browser Video Cutter (guaranteed real cut video on Cloudflare Pages and offline)
    if (uploadedFile) {
      try {
        setRenderProgress(15);
        const { blob, extension, durationSeconds } = await renderVideoCutsInBrowser(
          uploadedFile,
          finalRanges,
          (pct) => setRenderProgress(pct)
        );

        const cutVideoUrl = URL.createObjectURL(blob);
        const filename = `resumen-${activeJob.id.substring(0, 8)}.${extension}`;

        setDownloadUrl(cutVideoUrl);
        setDownloadFilename(filename);
        setRenderProgress(100);
        setIsRendering(false);

        setActiveJob((prev) => prev ? {
          ...prev,
          status: 'completed',
          outputKey: filename,
          durationSeconds: durationSeconds,
          aiPlan: {
            targetDurationSeconds: activeJob.targetDurationSeconds || 18,
            keepRanges: activeJob.settings.mandatoryRanges,
            selectedRanges: finalRanges,
            estimatedDurationSeconds: durationSeconds
          }
        } : null);

        fetchBetaStats();
        toast.success('¡Vídeo completado!', 'Tu resumen ha sido editado con éxito y está listo para descargar.');
        return;
      } catch (browserErr: any) {
        console.error('Error durante la edición en el navegador:', browserErr);
        setIsRendering(false);
        setActiveJob((prev) => prev ? {
          ...prev,
          status: 'failed',
          errorMessage: browserErr.message || 'Error al ensamblar los fragmentos en el navegador.'
        } : null);
        toast.error('Error al editar el vídeo', browserErr.message || 'No se pudo completar el recorte.');
        return;
      }
    }

    setIsRendering(false);
    setActiveJob((prev) => prev ? {
      ...prev,
      status: 'failed',
      errorMessage: 'No se encontró el metraje original para recortar.'
    } : null);
  };

  // Upgrade Handler
  const handleUpgradeToPremium = async () => {
    if (!user) {
      toast.info('Inicio de sesión requerido', 'Inicia sesión con Google para acceder al Plan Premium.');
      handleGoogleLogin();
      return;
    }

    try {
      const res = await fetch('/api/billing/create-checkout', { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        toast.error('Error al conectar con Stripe', data?.error || 'No se pudo iniciar la sesión de pago.');
      }
    } catch (err: any) {
      toast.error('Error de red', err?.message || 'Error al conectar con el servidor.');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex flex-col justify-between selection:bg-zinc-800 selection:text-white">
      {/* Top Persistent Beta Banner */}
      <BetaBanner
        completedCount={betaStats.completedCount}
        totalLimit={betaStats.totalLimit}
        onFeedbackClick={() => handleNavigate('legal', 'faq')}
      />

      {/* Navbar with full tabs */}
      <Navbar
        user={user}
        quota={quota}
        currentView={currentView}
        legalTab={legalTab}
        onNavigate={handleNavigate}
        onLoginClick={handleGoogleLogin}
        onLogoutClick={handleLogout}
        onUpgradeClick={() => handleNavigate('pricing')}
        onSettingsClick={user ? () => setShowSettings(true) : undefined}
      />

      {/* Main Content Area */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-10 sm:py-16">
        
        {/* VIEW: HOME (For unauthenticated visitors) */}
        {currentView === 'home' && (
          <div className="space-y-24 sm:space-y-36 lg:space-y-48 pb-20 sm:pb-32">
            <Hero
              onStartClick={() => {
                if (user) {
                  setCurrentView('workflow');
                  setWorkflowStep('upload');
                } else {
                  handleGoogleLogin();
                }
              }}
              onGuidesClick={() => handleNavigate('guides')}
            />

            <div className="reveal-on-scroll">
              <PricingTable onSelectPlan={(p) => {
                if (p === 'premium') handleUpgradeToPremium();
                else if (!user) handleGoogleLogin();
                else setCurrentView('workflow');
              }} />
            </div>
          </div>
        )}

        {/* VIEW: PRICING */}
        {currentView === 'pricing' && (
          <div>
            <PricingTable
              currentPlan={user?.plan || 'free'}
              onSelectPlan={(plan) => {
                if (plan === 'premium') handleUpgradeToPremium();
                else setCurrentView('dashboard');
              }}
            />
          </div>
        )}

        {/* VIEW: DASHBOARD */}
        {currentView === 'dashboard' && user && quota && (
          <div className="space-y-4">
            <DashboardView
              user={user}
              quota={quota}
              jobs={jobs}
              onStartNewJob={() => {
                setCurrentView('workflow');
                setWorkflowStep('upload');
                setActiveJob(null);
              }}
              onUpgradeClick={() => setCurrentView('pricing')}
              onSelectJob={(j) => {
                setActiveJob(j);
                setCurrentView('workflow');
                if (j.status === 'completed' || j.status === 'failed') setWorkflowStep('render');
                else if (j.status === 'review_pending') setWorkflowStep('review');
                else setWorkflowStep('processing');
              }}
            />
          </div>
        )}

        {/* VIEW: WORKFLOW (The Video Summarizer Experience) */}
        {currentView === 'workflow' && (
          <div className="max-w-3xl mx-auto space-y-6">
            
            {/* Step navigation / breadcrumbs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between border-b border-zinc-800/80 pb-3 gap-3">
              <button
                onClick={() => setCurrentView('dashboard')}
                className="text-xs text-zinc-400 hover:text-white transition-colors flex items-center gap-1.5 self-start"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Volver al panel</span>
              </button>
              <div className="flex items-center gap-1 bg-zinc-900/60 p-1 rounded-xl border border-zinc-800 text-xs overflow-x-auto">
                <span className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${workflowStep === 'upload' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}>
                  1. Subida
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${workflowStep === 'settings' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}>
                  2. Ajustes
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${workflowStep === 'processing' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}>
                  3. IA
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${workflowStep === 'review' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}>
                  4. Cortes
                </span>
                <span className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${workflowStep === 'render' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}>
                  5. Resultado
                </span>
              </div>
            </div>

            {/* STEP 1: UPLOAD */}
            {workflowStep === 'upload' && (
              <Uploader
                quota={quota}
                onFileSelected={handleFileSelected}
                uploadProgress={uploadProgress}
                isUploading={isUploading}
              />
            )}

            {/* STEP 2: SETTINGS */}
            {workflowStep === 'settings' && (
              <SettingsStep
                maxDurationSeconds={quota?.maxOutputDurationSeconds || 600}
                actualVideoDurationSeconds={activeJob?.durationSeconds || 38}
                onSubmit={handleSettingsSubmit}
                isLoading={isProcessing}
              />
            )}

            {/* STEP 3: PROCESSING WITH AI */}
            {workflowStep === 'processing' && activeJob && (
              <ProcessingStepper status={activeJob.status} />
            )}

            {/* STEP 4: INTERACTIVE CUT PLAN EDITOR */}
            {workflowStep === 'review' && activeJob && activeJob.aiPlan && (
              <CutPlanEditor
                plan={activeJob.aiPlan}
                totalDurationSeconds={activeJob.durationSeconds || 38}
                onConfirmRender={handleConfirmRender}
                onRegenerate={() => {
                  if (activeJob) {
                    setWorkflowStep('settings');
                  }
                }}
                isRendering={activeJob.status === 'rendering'}
              />
            )}

            {/* STEP 5: RENDER & DOWNLOAD */}
            {workflowStep === 'render' && activeJob && (
              <RenderStep
                job={activeJob}
                downloadUrl={downloadUrl}
                originalVideoUrl={localVideoUrl}
                isRendering={isRendering}
                renderProgress={renderProgress}
                downloadFilename={downloadFilename}
                userEmail={user?.email}
                onNewJob={() => {
                  setActiveJob(null);
                  setDownloadUrl(null);
                  setUploadedFile(null);
                  setLocalVideoUrl(null);
                  setIsRendering(false);
                  setRenderProgress(0);
                  setWorkflowStep('upload');
                  setCurrentView('dashboard');
                }}
              />
            )}

          </div>
        )}

        {/* VIEW: LEGAL (Dedicated Full Page for Privacy, Terms, Cookies & FAQ) */}
        {currentView === 'legal' && (
          <LegalView
            initialTab={legalTab}
            onBack={() => handleNavigate(user ? 'dashboard' : 'home')}
          />
        )}

        {/* VIEW: GUIDES & BLOG (Editorial Content Hub for AdSense & Community) */}
        {currentView === 'guides' && (
          <GuidesView
            onBack={() => handleNavigate(user ? 'dashboard' : 'home')}
            onOpenPricing={() => handleNavigate('pricing')}
          />
        )}

      </main>

      {/* Krayson Studio Footer */}
      <Footer onNavigate={handleNavigate} />

      {/* User Settings Modal */}
      {showSettings && user && quota && (
        <UserSettingsModal
          user={user}
          quota={quota}
          onClose={() => setShowSettings(false)}
          onLogout={() => { setShowSettings(false); handleLogout(); }}
          onUpgrade={() => { setShowSettings(false); handleNavigate('pricing'); }}
          onAccountDeleted={() => { setShowSettings(false); handleLogout(); }}
        />
      )}

      {/* First-Visit Beta Disclaimer & Information Modal */}
      <BetaDisclaimerModal
        completedCount={betaStats.completedCount}
        onClose={() => {}}
      />

      {/* Cookie Banner (Opens dedicated full-page Cookies/AdSense section) */}
      <CookieBanner onOpenPrivacy={() => handleNavigate('legal', 'cookies')} />

      {/* Global Toast Notifications Container */}
      <ToastContainer />
    </div>
  );
}
